<script lang="ts">
    import {
        Page,
        Block,
        Link,
        List,
        ListInput,
        ListItem,
        Button,
        Tabs,
        Tab,
        Toolbar,
        Actions,
        ActionsLabel,
        ActionsButton,
        ActionsGroup,
        Navbar,
        NavTitle,
        NavRight,
        NavLeft,
        Preloader,
        f7,
        f7ready
    } from 'framework7-svelte';
    import {onDestroy, onMount, tick} from 'svelte';
    import type { Router } from 'framework7/types';
    import { _ } from 'svelte-i18n';
    
    import ActionBar from '../components/ActionBar.svelte';
    import InsightFrame from '../components/InsightFrame.svelte';
    import {
        PERMISSION_VIEW_PROJECTS,
        PERMISSION_VIEW_INSIGHT,
        PERMISSION_MODIFY_PROJECTS,
        PERMISSION_VIEW_DASHBOARD
    } from '../utils/permissions';
    import logo from '../assets/lisa-buko.svg';
    import logodark from '../assets/lisa-buko-dark.svg';
    import { darkmode } from '../utils/darkmode';
    import AppSettings from '../components/AppSettings.svelte';
    import InsightsDashboard from '../components/InsightsDashboard.svelte';
    import {beginInProgress, endInProgress} from "../utils/notify";
    import {getLegendColorRGB} from "../utils/style";
    import type {IRouteInfo} from "../utils/types";
    import {parseRouteMeasurements, parseSectionMeasurements} from "../helpers/measurement-parser.helper";
    import {projectStore} from "../stores/project.store";
    import {measurementMomentsStore} from "../stores/measurement-moments.store";
    import {measurementMomentExpandedStore} from "../stores/measurement-moment-expanded.store";
    import {sectionMeasurementMomentsStore} from "../stores/section-measurement-moments.store";
    import {sectionMeasurementsMappedStore} from "../stores/section-measurements-mapped.store";
    import {routeMeasurementsMappedStore} from "../stores/route-measurements-mapped.store";
    import {sectionMeasurementsDateTimesStore} from "../stores/section-measurement-date-times.store";
    import type {Route} from "../types/projectItems/route.type";
    import {deleteProject, postNewProject, updateProjectName} from "../api/projects.api";
    import {routeInfoStore} from "../stores/route-info.store";
    
    export let projectId: string;
    export let f7router: Router.Router | undefined;
    
    type ProjectTab = 'general' | 'insights' | 'dashboard';

    let cleanedUp = false;
    let activeTab: ProjectTab = 'general';
    let projectName: string = '';
    let measurementRefreshInterval: ReturnType<typeof setInterval> | undefined;
    
    let dashboardCanMount = false;
    let insightsCanMount = false;
    let initialLoad = true;
    
    let mountTimer: ReturnType<typeof setTimeout> | undefined;
    let previousActiveTab: ProjectTab | undefined;

    $: if (activeTab !== previousActiveTab) {
        previousActiveTab = activeTab;
        clearTimeout(mountTimer);

        dashboardCanMount = false;
        insightsCanMount = false;

        if (activeTab === 'dashboard') {
            mountTimer = setTimeout(() => {
                dashboardCanMount = true;
            }, 0);
        }

        if (activeTab === 'insights') {
            mountTimer = setTimeout(() => {
                insightsCanMount = true;
            }, 0);
        }
    }

    $: isnotchanged = projectName == $projectStore.data?.name;
    $: isNew = projectId === 'new';
    
    onMount(async () => {
        f7ready(async (f7) => {
            requestAnimationFrame(async () => {
                await syncTabWithRoute(f7.views.main.router.currentRoute?.path);
            });
        });

        if(isNew) {
            projectName = $_('pages.project.NEW_PROJECT');
            initialLoad = false;
            return;
        }
        
        await refreshAll();
        initialLoad = false;
    });

    onDestroy(() => {
        clearTimeout(mountTimer);
        clearInterval(measurementRefreshInterval);
    });

    function cleanupProjectPage() {
        if (cleanedUp) return;
        cleanedUp = true;

        if (isLeavingProjectPage()) {
            clearInterval(measurementRefreshInterval);
            clearAllStores();
        }
    }

    function isLeavingProjectPage() {
        const nextPath = f7.views.main.router.currentRoute?.path;
        return !nextPath?.startsWith(`/project/${projectId}`);
    }

    async function refreshAll() {
        if (projectId === $projectStore.data?.id) {
            projectName = $projectStore.data.name;
            setMeasurementRefreshInterval();
            return;
        }

        beginInProgress();

        try {
            await loadProject();
            await loadMeasurementMoments();
            await loadSelectedMeasurementMoment();
            await loadSectionMeasurements();
        } finally {
            endInProgress();
        }
    }
    
    async function refreshProject() {
        beginInProgress();

        try {
            await loadProject()    
        }
        finally {
            endInProgress();   
        }
    }

    async function refreshMeasurements() {
        beginInProgress();

        try {
            await loadMeasurementMoments();
            await loadSelectedMeasurementMoment();
            await loadSectionMeasurements();
        } finally {
            endInProgress();
        }
    }
    
    async function loadProject() {
        await projectStore.load(projectId);
        projectName = $projectStore.data.name;
        routeInfoStore.set(setRouteInfo($projectStore.data.routes));
        setMeasurementRefreshInterval();
    }
    
    async function loadMeasurementMoments() {
        await measurementMomentsStore.load(projectId);
    }
    
    async function loadSelectedMeasurementMoment() {
        if($measurementMomentsStore.data?.length < 1)
            return;

        let previousMostRecentMoment = $measurementMomentsStore.data[$measurementMomentsStore.data.length - 2];
        if(!$measurementMomentExpandedStore.data || $measurementMomentExpandedStore.data.id == previousMostRecentMoment?.id) {
            let mostRecentMoment = $measurementMomentsStore.data[$measurementMomentsStore.data.length - 1];
            await setSelectedMeasurementMoment(mostRecentMoment.id);
        }
    }
    
    async function setSelectedMeasurementMoment(momentId: string) {
        await measurementMomentExpandedStore.load(momentId);
    }
    
    async function loadSectionMeasurements() {
        if($measurementMomentsStore.data?.filter(moment => moment.calledHereRouting).length < 1)
            return;
        
        await sectionMeasurementMomentsStore.load(projectId);
        
        const sectionMeasurements = $sectionMeasurementMomentsStore.data;
        
        sectionMeasurementsMappedStore.set(
            parseSectionMeasurements(
                $projectStore.data.gates,
                $projectStore.data.routes.flatMap(route => route.routeGates),
                $projectStore.data.routes.flatMap(route => route.sections),
                sectionMeasurements
            )
        );
        routeMeasurementsMappedStore.set(
            parseRouteMeasurements(
                $projectStore.data.routes.flatMap(route => route.sections),
                $projectStore.data.routes,
                sectionMeasurements
            )
        );
        
        const distinctDates = [
            ...new Set(sectionMeasurements.map((measurement) => new Date(measurement.moment).getTime()))
        ]
        
        sectionMeasurementsDateTimesStore.set(
            distinctDates
                .map(date => new Date(date))
                .sort((a, b) => a.getTime() - b.getTime())
        );
    }
    
    function clearAllStores() {
        projectStore.clear();
        measurementMomentsStore.clear();
        measurementMomentExpandedStore.clear();
        sectionMeasurementMomentsStore.clear(); 
        
        sectionMeasurementsDateTimesStore.set([]);
        sectionMeasurementsMappedStore.set([]);
        routeMeasurementsMappedStore.set([]);
        
    }

    function getTabFromPath(path?: string): ProjectTab {
        if (path?.includes('/dashboard')) return 'dashboard';
        if (path?.includes('/insights')) return 'insights';

        return 'general';
    }
    
    async function syncTabWithRoute(currentPath: string) {
        activeTab = getTabFromPath(currentPath);
        if ($projectStore.loading && !isNew) {
            return;
        }

        await tick();

        f7.toast.close('.toast');
        f7.tab.show(`#tab-${activeTab}-${projectId}`);
    }

    function navigateToTab(route: string) {
        f7.views.main.router.navigate(route, { animate: false });
    }

    function setMeasurementRefreshInterval() {
        clearInterval(measurementRefreshInterval);
        
        const intervals = [
            $projectStore.data.mapboxCongestionInterval,
            $projectStore.data.ndwIntensityInterval,
            $projectStore.data.hereRoutingInterval,
            $projectStore.data.hereTrafficInterval
        ]
        
        const activeIntervals = intervals.filter(interval => interval > 0);
        // We don't want to set a refresh interval if the project has no active measurements
        if (activeIntervals.length == 0) return;
        
        const shortestInterval = Math.min(
            ...activeIntervals
        );
        
        measurementRefreshInterval = setInterval(async () => {
            await refreshMeasurements();
        }, shortestInterval * 60000);
    }

    function setRouteInfo(routes: Route[]) {
        const routeInfo: { [id: string]: IRouteInfo } = {};
        
        routes.forEach((route, index) => {
            if (!routeInfo[route.id]) {
                routeInfo[route.id] = {
                    hidden: false,
                    color: getLegendColorRGB(index)
                }
            }
        });

        return routeInfo;
    }
    
    function updateName(e: CustomEvent) {
        projectName = (e.target as any).value;
    }

    async function handleSubmitProject() {
        if (projectId === 'new') {
            const newProjectId = await postNewProject(projectName);
            f7router?.navigate(`/project/${newProjectId}/insights`);
        } else {
            await updateProjectName(projectId, projectName);
            f7router?.navigate(`/project/${projectId}`);
        }
    }

    function handleRevertChanges() {
        projectName = $projectStore.data?.name ?? '';
    }

    async function handleDeleteProject() {
        f7.actions.close('#confirm-delete');
        await deleteProject(projectId);
        
        f7router?.navigate(`/projects`);
    }
</script>

<Page on:pageBeforeOut={cleanupProjectPage}>
    {#if initialLoad}
        <Block class="tab-loading">
            {$_('pages.project.LOADING_LINE_1_GENERIC')}
            <br />
            {$_('pages.project.LOADING_LINE_2')}
            <br />
            {$_('pages.project.LOADING_LINE_3')}
            <br/>
        </Block>
        <Block class="tab-loading">
            <Preloader />
        </Block>
    {:else}
        <Navbar>
            <NavLeft class="col-33">
                <Link href="/">
                    <img src={$darkmode ? logodark : logo} alt="Traffic Life Logo" />
                </Link>
                <span class="project-name">
                {projectName || 'Aan het laden...'}
            </span>
            </NavLeft>
            <NavTitle class="col-33">
                <Toolbar tabbar top>
                    {#if $PERMISSION_MODIFY_PROJECTS}
                        <Link
                            tabLink={`#tab-general-${projectId}`}
                            tabLinkActive={activeTab === 'general'}
                            on:click={() => { navigateToTab(`/project/${projectId}`) }}>
                            {$_('shared.GENERAL')}
                        </Link>
                    {/if}
                    {#if !isNew}
                        {#if $PERMISSION_VIEW_INSIGHT}
                            <Link
                                tabLink={`#tab-insights-${projectId}`}
                                tabLinkActive={activeTab === 'insights'}
                                on:click={() => { navigateToTab(`/project/${projectId}/insights`) }}>
                                {$_('shared.INSIGHTS')}
                            </Link>
                        {/if}
                        {#if $PERMISSION_VIEW_DASHBOARD}
                            <Link
                                tabLink={`#tab-dashboard-${projectId}`}
                                tabLinkActive={activeTab === 'dashboard'}
                                on:click={() => { navigateToTab(`/project/${projectId}/dashboard`) }}>
                                {$_('shared.DASHBOARD')}
                            </Link>
                        {/if}
                    {/if}
                </Toolbar>
            </NavTitle>
            <NavRight>
                {#if $PERMISSION_VIEW_PROJECTS}
                    <Link href="/projects">
                        {$_('shared.PROJECTS')}
                    </Link>
                {/if}
                <hr class="divider--direction-vertical" />
                <AppSettings />
            </NavRight>
        </Navbar>

        <Tabs>
            <Tab id={`tab-general-${projectId}`} tabActive={isNew || activeTab === 'general'}>
                {#if !isNew}
                    <ActionBar>
                        {#if $PERMISSION_MODIFY_PROJECTS}
                            <Button outline class="display-inline-flex padding-horizontal" actionsOpen="#confirm-delete" iconMaterial="delete">
                                {$_('shared.DELETE')}
                            </Button>
                        {/if}
                    </ActionBar>
                {/if}

                <Block style="max-width: 800px">
                    <List inline-labels noHairlinesMd>
                        <ListInput label="Naam" type="text" outline clearButton value={projectName} onInput={updateName} disabled={!$PERMISSION_MODIFY_PROJECTS}
                        ></ListInput>
                        {#if $PERMISSION_MODIFY_PROJECTS}
                            <ListItem>
                                <Button fill on:click={handleSubmitProject} disabled={isnotchanged}>
                                    {$_('shared.SAVE')}
                                </Button>
                                <Button on:click={handleRevertChanges} disabled={isnotchanged}>
                                    {$_('shared.RESET')}
                                </Button>
                            </ListItem>
                        {/if}
                    </List>
                </Block>
            </Tab>

            {#if !isNew}
                <Tab id={`tab-insights-${projectId}`} tabActive={activeTab === 'insights'}>
                    {#if activeTab === 'insights'}
                        {#if insightsCanMount}
                            <InsightFrame
                                    {setSelectedMeasurementMoment}
                                    {refreshProject}
                                    {refreshMeasurements}
                            />
                        {:else}
                            <Block class="tab-loading">
                                {$_('pages.project.LOADING_LINE_1_SPECIFIC', { values: { projectName, target: $_('shared.INSIGHTS') } } )}
                                <br />
                                {$_('pages.project.LOADING_LINE_2')}
                                <br />
                                {$_('pages.project.LOADING_LINE_3')}
                                <br/>
                            </Block>
                            <Block class="tab-loading">
                                <Preloader />
                            </Block>
                        {/if}
                    {/if}
                </Tab>

                <Tab id={`tab-dashboard-${projectId}`} tabActive={activeTab === 'dashboard'}>
                    {#if activeTab === 'dashboard'}
                        {#if dashboardCanMount}
                            <InsightsDashboard />
                        {:else}
                            <Block class="tab-loading">
                                {$_('pages.project.LOADING_LINE_1_SPECIFIC', { values: { projectName, target: $_('shared.DASHBOARD') } } )}
                                <br />
                                {$_('pages.project.LOADING_LINE_2')}
                                <br />
                                {$_('pages.project.LOADING_LINE_3')}
                                <br/>
                            </Block>
                            <Block class="tab-loading">
                                <Preloader />
                            </Block>
                        {/if}
                    {/if}
                </Tab>
            {/if}
        </Tabs>

        <Actions id="confirm-delete">
            <ActionsGroup>
                <ActionsLabel>
                    {$_('pages.project.DELETE_PROJECT_CONFIRMATION')}
                </ActionsLabel>
                <ActionsButton class="delete-action" on:click={handleDeleteProject}>
                    {$_('shared.DELETE')}
                </ActionsButton>
                <ActionsButton color="red">
                    {$_('shared.CANCEL')}
                </ActionsButton>
            </ActionsGroup>
        </Actions>
    {/if} 
</Page>

<style>
    :global(.col-33) {
        width: 33%;
    }
    
    :global(.tab-loading) {
        min-height: 240px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
    }

    :global(.delete-action) {
        font-weight: 700;
    }
    
    .project-name {
        color: var(--proj-secondary);
    }
</style>
